<script setup lang="ts">

import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import Contact from './Contact.vue';

// Variable Declarations
const router = useRouter();
const imageDeck = ref(["/images/Volume-2/Science.png", "/images/Volume-2/Arts.png"]);
const currentImage = ref(imageDeck.value[0]);
const currentIndex = ref(0); 
let intervalId : any;

// Event Handlers
const showSection = (section: string) => {
    router
        .push({ name: section })
        .catch((err) => {
            console.log(err);
        });
};

// Lifecycle Hooks
onMounted(() => {
    intervalId = setInterval(rotateImages, 4000);
});

onUnmounted(() => {
    clearInterval(intervalId);
});

//Function Declarations
const rotateImages = () => {
    currentIndex.value = (currentIndex.value + 1) % imageDeck.value.length;
    currentImage.value = imageDeck.value[currentIndex.value];
}

</script>

<template>
    <div class="w-full flex flex-col justify-center">
        <!-- Hero Section -->
        <section class="w-full min-h-[60vh] sm:min-h-[80vh] flex flex-col justify-center items-center bg-gradient-to-b from-white via-[#e68c74]/20 to-[#e68c74] py-12 sm:py-20 px-4">
            <div @click="showSection('news')" class="cursor-pointer group">
                <div class="w-full h-fit flex justify-center items-center mb-6">
                    <div class="sm:hidden image-container object-contain rounded-lg shadow-2xl border-2 border-[#012331]/20" :style="`background-image: url('${currentImage}')`"></div> 
                </div>
                <div class="w-full flex-row justify-center gap-6 hidden sm:flex">
                    <img class="h-[45rem] max-w-lg border-2 border-[#012331]/20 rounded-lg shadow-2xl " src="/images/Volume-2/Arts.png" alt="Arts & Humanities Volume 1" />
                    <img class="h-[45rem] max-w-lg border-2 border-[#012331]/20 rounded-lg shadow-2xl " src="/images/Volume-2/Science.png" alt="Science & Medicine Volume 1" />
                </div>
                <div class="w-full space-y-2 flex justify-center mt-6 sm:mt-8">
                    <p class="font-satoshibold text-xl sm:text-2xl lg:text-3xl text-[#012331] transition-colors group-hover:text-white group-hover:underline">
                        View our current issues here
                    </p>
                </div>
            </div>
        </section>

        <!-- Who We Are Section -->
        <section class="w-full min-h-[50vh] sm:min-h-[70vh] bg-[#e68c74] flex justify-center items-center text-white py-16 sm:py-24 px-4 sm:px-6">
            <div class="w-full max-w-7xl flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
                <div class="w-full lg:w-3/4 flex flex-col space-y-6">
                    <h2 class="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-satoshibold">Who we are</h2>
                    <div class="text-base sm:text-lg lg:text-xl font-satoshi flex flex-col space-y-6 leading-relaxed">
                        <p>
                            The Journal of Undergraduate Inquiry is a student-run academic journal aimed to allow undergraduate students to showcase their research, creative, and scholarly work. We aim to publish works that exhibit excellence in intellectual rigor, originality, and contribution to their respective fields, fostering a culture of academic curiosity and innovation.
                        </p>
                        <p>
                            Our journal accepts submissions across a wide range of disciplines, encouraging interdisciplinary dialogue and collaboration. By providing a platform for undergraduate students to publish their work, we hope to inspire future research endeavors and academic pursuits. We are committed to maintaining a high standard of quality and integrity in all published works, ensuring that each piece is thoroughly reviewed and critiqued by peers and faculty members.
                        </p>
                        <p>
                            Through this inclusive approach, The Journal of Undergraduate Inquiry aims to highlight the diverse talents and perspectives of undergraduate students, contributing to a vibrant and dynamic academic community.
                        </p>
                    </div>
                </div>
                <div class="w-full lg:w-1/4 flex justify-center lg:justify-end">
                    <img class="w-full max-w-xs lg:max-w-sm object-contain" src="/images/Team.png" alt="JUI Team" />
                </div>
            </div>
        </section>

        <!-- Mission Section -->
        <section class="w-full min-h-[40vh] sm:min-h-[60vh] bg-gradient-to-b from-[#e68c74] via-[#e68c74] to-[#012331] flex justify-center items-center text-white py-16 sm:py-24 px-4 sm:px-6">
            <div class="w-full max-w-7xl flex flex-col sm:flex-row gap-8 sm:gap-12 lg:gap-24 items-center">
                <div class="w-full sm:w-auto flex justify-center sm:justify-start">
                    <img class="h-40 sm:h-56 lg:h-72 object-contain" src="/images/Microscope.png" alt="Microscope" />
                </div>
                <div class="w-full sm:w-3/5 flex flex-col space-y-6">
                    <h2 class="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-satoshibold">Our Mission</h2>
                    <p class="text-base sm:text-lg lg:text-xl font-satoshi leading-relaxed">
                        At JUI, we're not just publishing papers; we're building the next generation of researchers, scientists, and artists. Our mission is to amplify undergraduate voices, turning groundbreaking ideas into impactful realities.
                    </p>
                </div>
            </div>
        </section>

        <!-- Journal Sections -->
        <section class="w-full min-h-[50vh] bg-[#012331] flex justify-center text-white py-16 sm:py-24 px-4 sm:px-6">
            <div class="w-full max-w-6xl flex flex-col items-center">
                <h2 class="text-2xl sm:text-3xl lg:text-4xl font-satoshibold mb-12 sm:mb-16 text-center">
                    Explore our Journal Sections
                </h2>
                <div class="w-full grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    <div class="w-full bg-white rounded-xl shadow-lg flex flex-col items-center p-6 sm:p-8 hover:shadow-xl">
                        <h3 class="mb-4 text-xl sm:text-2xl font-satoshibold text-[#012331]">
                            Science & Medicine
                        </h3>
                        <div class="w-full flex flex-col justify-between flex-grow space-y-6 font-satoshi">
                            <p class="text-[#012331] text-sm sm:text-base leading-relaxed">
                                Explore groundbreaking research and developments in biology, chemistry, and medical sciences that shape our understanding of life and health.
                            </p>
                            <div class="flex justify-center">
                                <button class="bg-[#e68c74] text-white rounded-lg px-6 py-3 font-satoshibold transition-all duration-300 hover:bg-[#e68c74]/90 hover:shadow-lg active:scale-95" @click="showSection('sciencemedicine')">
                                    Explore this Section
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="w-full bg-white rounded-xl shadow-lg flex flex-col items-center p-6 sm:p-8 hover:shadow-xl">
                        <h3 class="mb-4 text-xl sm:text-2xl font-satoshibold text-[#012331]">
                            Arts & Humanities
                        </h3>
                        <div class="w-full flex flex-col justify-between flex-grow space-y-6 font-satoshi">
                            <p class="text-[#012331] text-sm sm:text-base leading-relaxed">
                                Discover diverse perspectives in fine arts, literature, and social sciences that challenge conventional thinking and inspire creativity.
                            </p>
                            <div class="flex justify-center">
                                <button class="bg-[#e68c74] text-white rounded-lg px-6 py-3 font-satoshibold transition-all duration-300 hover:bg-[#e68c74]/90 hover:shadow-lg active:scale-95" @click="showSection('artshumanities')">
                                    Explore this Section
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Contact Section -->
        <section class="flex justify-center py-12 sm:py-16 bg-white">
            <div class="w-full max-w-4xl px-4 sm:px-6">
                <Contact />
            </div>
        </section>
    </div>
</template>

<style scoped>
.image-container {
    width: 85vw;
    max-width: 400px;
    height: 60vh;
    max-height: 500px;
    background-size: cover;
    background-position: center;
    transition: background 1s ease-in-out, transform 0.3s ease;
}

@media screen and (min-width: 640px) {
    .image-container {
        width: 50vw;
        max-width: 450px;
        height: 65vh;
        max-height: 600px;
    }
}

@media screen and (min-width: 1024px) {
    .image-container {
        width: 45vw;
        max-width: 500px;
    }
}
</style>